import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Phone, EnvelopeSimple, Clock, MapPin } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const PROGRAMS = ['Super Barriguinhas', 'Super Mamãs', 'Super Pré Mamãs', 'Super Bebés', 'Geral'];
const DAYS = [
  { v: 'segunda', l: 'Seg' }, { v: 'terca', l: 'Ter' }, { v: 'quarta', l: 'Qua' },
  { v: 'quinta', l: 'Qui' }, { v: 'sexta', l: 'Sex' }, { v: 'sabado', l: 'Sáb' },
];
const PERIODS = [{ v: 'manha', l: 'Manhã' }, { v: 'tarde', l: 'Tarde' }, { v: 'indiferente', l: 'Indiferente' }];
const MODALITIES = [{ v: 'presencial', l: 'Presencial' }, { v: 'online', l: 'Online' }, { v: 'indiferente', l: 'Indiferente' }];
const STATUSES = ['pendente', 'contactada', 'agendada', 'descartada'];

const emptyForm = {
  name: '', origin: '', phone: '', email: '',
  available_days: [], preferred_period: 'indiferente',
  modality: 'indiferente', program: 'Geral', notes: '', status: 'pendente',
};

export default function ListaEsperaPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState('');

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/waitlist`);
      setItems(data || []);
    } catch { toast.error('Erro a carregar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || '', origin: it.origin || '', phone: it.phone || '', email: it.email || '',
      available_days: it.available_days || [], preferred_period: it.preferred_period || 'indiferente',
      modality: it.modality || 'indiferente', program: it.program || 'Geral',
      notes: it.notes || '', status: it.status || 'pendente',
    });
    setShowForm(true);
  };

  const toggleDay = (d) => setForm(f => ({
    ...f,
    available_days: f.available_days.includes(d) ? f.available_days.filter(x => x !== d) : [...f.available_days, d],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome obrigatório'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/api/waitlist/${editing.id}`, form);
        toast.success('Atualizado');
      } else {
        await axios.post(`${API}/api/waitlist`, form);
        toast.success('Adicionada à lista');
      }
      setShowForm(false); setEditing(null); setForm(emptyForm); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro a guardar'); }
  };

  const remove = async (id) => {
    const it = items.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a entrada de "${it?.name || ''}" da lista de espera.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/waitlist/${id}`); toast.success('Removido'); fetch(); }
    catch { toast.error('Erro'); }
  };

  const filtered = filter
    ? items.filter(i => (i.name || '').toLowerCase().includes(filter.toLowerCase()) || (i.email || '').toLowerCase().includes(filter.toLowerCase()) || (i.phone || '').includes(filter))
    : items;

  return (
    <div className="p-5 max-w-6xl mx-auto" data-testid="waitlist-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl text-brand-text">Lista de Espera</h1>
          <p className="text-xs text-brand-muted">{items.length} {items.length === 1 ? 'pessoa' : 'pessoas'} aguardam contacto</p>
        </div>
        <button onClick={openCreate} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-1.5" data-testid="waitlist-add-btn">
          <Plus size={14} /> Nova entrada
        </button>
      </div>

      <Input
        type="text"
        placeholder="Pesquisar por nome, email ou telemóvel..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="rounded-2xl border-brand-border mb-4"
        data-testid="waitlist-search"
      />

      {loading ? (
        <div className="text-center py-8 text-brand-muted">A carregar...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-2xl p-8 text-center text-brand-muted text-sm" data-testid="waitlist-empty">
          {filter ? 'Sem resultados.' : 'Lista de espera vazia.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(it => (
            <div key={it.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`waitlist-row-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-medium text-brand-text">{it.name}</h3>
                    <span className="text-[10px] bg-brand-mint/20 text-brand-green px-2 py-0.5 rounded-full">{it.program}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${it.status === 'pendente' ? 'bg-amber-50 text-amber-700' : it.status === 'contactada' ? 'bg-blue-50 text-blue-700' : it.status === 'agendada' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{it.status}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${it.entry_type === 'public' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`} data-testid={`waitlist-type-${it.id}`}>{it.entry_type === 'public' ? 'Entrada pública' : 'Entrada manual'}</span>
                    {it.converted_to_patient && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`waitlist-converted-${it.id}`}>Convertida</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-brand-muted">
                    {it.phone && <span className="flex items-center gap-1"><Phone size={11} /> {it.phone}</span>}
                    {it.email && <span className="flex items-center gap-1"><EnvelopeSimple size={11} /> {it.email}</span>}
                    <span className="flex items-center gap-1"><Clock size={11} /> {PERIODS.find(p => p.v === it.preferred_period)?.l || it.preferred_period}</span>
                    <span className="flex items-center gap-1"><MapPin size={11} /> {MODALITIES.find(m => m.v === it.modality)?.l || it.modality}</span>
                  </div>
                  {it.available_days?.length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {it.available_days.map(d => (
                        <span key={d} className="text-[9px] bg-brand-bg border border-brand-border text-brand-muted px-1.5 py-0.5 rounded">{DAYS.find(x => x.v === d)?.l || d}</span>
                      ))}
                    </div>
                  )}
                  {it.origin && <p className="text-[11px] text-brand-muted mt-1.5"><strong>Origem:</strong> {it.origin}</p>}
                  {it.notes && <p className="text-[11px] text-brand-muted mt-1 italic">"{it.notes}"</p>}
                  <p className="text-[10px] text-brand-placeholder mt-1.5">Adicionada {fmtDate(it.created_at)}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(it)} className="p-1.5 text-brand-placeholder hover:text-brand-green" title="Editar" data-testid={`waitlist-edit-${it.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => remove(it.id)} className="p-1.5 text-brand-placeholder hover:text-red-500" title="Remover" data-testid={`waitlist-delete-${it.id}`}><Trash size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="rounded-3xl border-brand-border max-w-lg" data-testid="waitlist-form">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editing ? 'Editar entrada' : 'Nova entrada'}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome completo *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" required data-testid="waitlist-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Telemóvel</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-xl border-brand-border" data-testid="waitlist-phone" />
              </div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl border-brand-border" data-testid="waitlist-email" />
              </div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Origem / referência</Label>
              <Input value={form.origin} onChange={e => setForm({ ...form, origin: e.target.value })} placeholder="Ex: Instagram, amiga, anúncio..." className="rounded-xl border-brand-border" data-testid="waitlist-origin" />
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Programa de interesse</Label>
              <select value={form.program} onChange={e => setForm({ ...form, program: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="waitlist-program">
                {PROGRAMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Dias disponíveis</Label>
              <div className="flex gap-1.5 flex-wrap">
                {DAYS.map(d => (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.available_days.includes(d.v) ? 'bg-brand-green text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`waitlist-day-${d.v}`}>{d.l}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Horário preferido</Label>
                <select value={form.preferred_period} onChange={e => setForm({ ...form, preferred_period: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="waitlist-period">
                  {PERIODS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Modalidade</Label>
                <select value={form.modality} onChange={e => setForm({ ...form, modality: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="waitlist-modality">
                  {MODALITIES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </div>
            </div>
            {editing && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Estado</Label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="waitlist-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="rounded-xl border-brand-border" data-testid="waitlist-notes" />
            </div>
            <button type="submit" className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="waitlist-save">
              {editing ? 'Guardar alterações' : 'Adicionar à lista'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
